import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import { useAuth } from "../../contexts/AuthContext";
import { getLgbtqContent, getLgbtqArticle, orderForPartner } from "../../data/lgbtqContent";
import { ChevronLeft, ChevronRight, HeartHandshake } from "lucide-react";

/**
 * LGBTQ+ Sanctuary module — six trauma-informed articles, bilingual.
 *
 * Two surfaces share this file for cohesion:
 *   /sanctuary/lgbtq          → hub (list of articles)
 *   /sanctuary/lgbtq/:slug    → individual article
 *
 * Article order is personalised to profile.partner_gender. The personalised
 * lift is gentle — every article remains visible; only the order changes.
 */

export function LgbtqHub() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const all = getLgbtqContent(i18n.language);
  const ordered = orderForPartner(profile?.partner_gender)
    .map((slug) => all.find((a) => a.slug === slug))
    .filter(Boolean);

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-lgbtq-hub">
        <button
          onClick={() => navigate("/sanctuary")}
          className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2"
        >
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("lgbtq.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">{t("lgbtq.h")}</h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("lgbtq.intro")}</p>
      </div>

      <div className="px-8 pb-10 space-y-3">
        {ordered.map((a) => (
          <button
            key={a.slug}
            data-testid={`lgbtq-article-${a.slug}`}
            onClick={() => navigate(`/sanctuary/lgbtq/${a.slug}`)}
            className="w-full text-left rounded-3xl bg-card border border-border p-5 hover:border-brand/50 transition-all"
          >
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-full bg-brand/10 text-brand shrink-0">
                <HeartHandshake className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg leading-snug">{a.title}</div>
                <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{a.lede}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-foreground/40 mt-2 shrink-0" />
            </div>
          </button>
        ))}

        <section className="mt-6 rounded-3xl bg-brand-soft/40 border border-brand/20 p-6" data-testid="lgbtq-gentle">
          <p className="font-script text-xl text-brand">{t("lgbtq.gentle_kicker")}</p>
          <p className="mt-1 font-serif italic text-foreground/85 leading-relaxed">{t("lgbtq.gentle_body")}</p>
        </section>
      </div>
    </AppShell>
  );
}

export function LgbtqArticle() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const a = getLgbtqArticle(slug, i18n.language);

  if (!a) {
    return (
      <AppShell>
        <div className="px-8 pt-16">
          <button onClick={() => navigate("/sanctuary/lgbtq")} className="text-brand">
            ← {t("lgbtq.back_to_hub")}
          </button>
          <p className="mt-4 text-foreground/70">{t("lgbtq.not_found")}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <article className="px-8 pt-16 pb-10" data-testid={`page-lgbtq-${slug}`}>
        <button
          onClick={() => navigate("/sanctuary/lgbtq")}
          className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2"
        >
          <ChevronLeft className="w-4 h-4" /> {t("lgbtq.back_to_hub")}
        </button>
        <p className="font-script text-2xl text-brand">{t("lgbtq.article_kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">{a.title}</h1>
        <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{a.lede}</p>

        <div className="mt-8 space-y-4">
          {a.sections.map((s, i) => (
            <section key={i} className="rounded-3xl bg-card border border-border p-6">
              <p className="text-xs uppercase tracking-widest text-foreground/55">{s.h}</p>
              {s.body && (
                <p className="mt-3 text-foreground/85 leading-relaxed">{s.body}</p>
              )}
              {Array.isArray(s.list) && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((it, j) => (
                    <li key={j} className="text-foreground/85 leading-relaxed flex items-start gap-2">
                      <span className="text-brand mt-1.5">·</span>
                      <span className="flex-1">{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-3xl bg-brand-soft/40 border border-brand/20 p-6">
          <p className="font-script text-xl text-brand">{t("lgbtq.gentle_kicker")}</p>
          <p className="mt-1 font-serif italic text-foreground/85 leading-relaxed">{t("lgbtq.gentle_body")}</p>
        </section>
      </article>
    </AppShell>
  );
}
